import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db78b4en6nb9f68e9aog";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：隐藏字段被填写则视为机器人，返回通用提示，不发送请求
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim().length > 0
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-background-200/70 bg-background-50 px-6 py-14 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-checkbox-circle-line text-3xl" />
        </span>
        <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">反馈已提交</h3>
        <p className="mt-2 max-w-md text-sm text-foreground-600">
          感谢你的反馈，我们会尽快查看并处理。如需进一步沟通，我们可能会通过你留下的邮箱与你联系。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          继续提交
        </button>
      </div>
    );
  }

  return (
    <form
      id="about-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="feedback-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="feedback-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="feedback-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="feedback-email"
            name="email"
            type="email"
            required
            placeholder="example@mail.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="feedback-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
          反馈类型
        </label>
        <select id="feedback-topic" name="topic" defaultValue="内容建议" className={inputClass}>
          <option value="内容建议">内容建议</option>
          <option value="问题反馈">问题反馈</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="feedback-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="feedback-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入你的建议或问题，最多 500 字"
          className={`${inputClass} resize-none`}
        />
      </div>

      <div className="contact-extra-field" aria-hidden="true">
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {status === "error" && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
          <i className="ri-error-warning-line mt-0.5 text-base" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-base" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}